import React, { useMemo } from 'react';
import { Button } from '@cp/ds/src/components/button';
import { Box, Typography } from '@mui/material';
import { CareerId } from '@common/const/catalog/career';
import { useForm } from 'react-hook-form';
import { TestType, Variant } from '@common/model/client/training/types';
import { AppFormProvider } from '@cp/utils/form';
import { FormField } from '@cp/ds/src/components/form';
import { CheckboxGroupItemConfig } from '@cp/ds/src/components/form/components/field/checkboxGroup/model';
import { useTheme } from '@cp/ds/src/hooks/useTheme';
import { MOBILE_BREAKPOINT_THRESHOLD } from '@cp/ds/src/theme';
import { StepHeader } from 'src/features/client/training/ui/stepHeader';
import { StepContainer } from '../stepContainer';
import { StepFooter } from '../stepFooter';
import { QuestionStepProps } from './model';
import { StepResult } from './stepResult';

type QuestionField = { name: string; variants?: Variant[] };
const variantToOption = (variant: Variant): CheckboxGroupItemConfig => ({ value: String(variant.value), label: variant.title });
const obj2arr = (obj: Record<string, boolean>): string[] => {
  return Object.keys(obj).filter((key) => obj[key]);
};
const arr2obj = (arr: number[]) => {
  return arr.reduce((acc, v) => {
    acc[v] = true;
    return acc;
  }, {});
};
const TextareaField: React.FC<QuestionField> = (props) => <FormField {...props} sx={{ width: '100%' }} type="textArea" />;
const CheckboxField: React.FC<QuestionField> = (props) =>
  props.variants ? (
    <FormField
      containerProps={{
        sx: {
          m: 0,
          maxWidth: '100%',
          display: 'grid',
          gap: 1.5,
          gridTemplateColumns: { xs: '1fr', [MOBILE_BREAKPOINT_THRESHOLD]: '1fr 1fr' },
        },
      }}
      itemSx={{ padding: '0!important' }}
      items={props.variants.map(variantToOption)}
      multiple
      name={props.name}
      transform={{
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        in: (v) => (typeof v === 'object' && v !== null ? obj2arr(v) : []),
        out: (v) => (Array.isArray(v) ? arr2obj(v) : {}),
      }}
      type="checkboxGroup"
    />
  ) : null;

const RadioboxField: React.FC<QuestionField> = (props) =>
  props.variants ? (
    <FormField
      containerProps={{
        sx: {
          m: 0,
          maxWidth: '100%',
          display: 'grid',
          gap: 1.5,
          gridTemplateColumns: { xs: '1fr', [MOBILE_BREAKPOINT_THRESHOLD]: '1fr 1fr' },
        },
      }}
      itemSx={{ padding: '0!important' }}
      items={props.variants.map(variantToOption)}
      multiple={false}
      name={props.name}
      transform={{
        in: (v: unknown) => {
          return typeof v === 'object' && v !== null ? obj2arr(v as Record<string, boolean>).pop() : undefined;
        },
        out: (v: unknown) => {
          return { [v as string]: true };
        },
      }}
      type="checkboxGroup"
    />
  ) : null;

export const QuestionStep: React.FC<QuestionStepProps> = ({ onSubmit, model, state, meta, values: defaultValues }) => {
  const questionSuggest = useMemo(() => {
    switch (model.type) {
      case TestType.Text:
        return 'Напишите в свободной форме';
      case TestType.SingleVariant:
        return 'Выберите один вариант';
      case TestType.Variants:
        return 'Выберите один или несколько вариантов';
      default:
        return model.questionDescription;
    }
  }, [model.type, model.questionDescription]);
  const theme = useTheme();
  const form = useForm({
    defaultValues,
    resolver: (values) => {
      const errors = {};
      if (!(Array.isArray(values[model.id]) ? (values[model.id] as Array<unknown>).length : values[model.id])) {
        errors[model.id] = { message: questionSuggest };
      }
      return { values, errors };
    },
  });

  const FieldComponent: (p: QuestionField) => JSX.Element | null = useMemo(() => {
    switch (model.type) {
      case TestType.Text:
        return TextareaField;
      case TestType.SingleVariant:
        return RadioboxField;
      case TestType.Variants:
        return CheckboxField;
      default:
        return () => null;
    }
  }, [model.type]);

  return (
    <AppFormProvider form={form}>
      <Box component="form" onSubmit={form.handleSubmit(onSubmit)} sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
        <StepContainer sx={{ flexGrow: 1 }}>
          <StepHeader
            blockTitle={meta.title}
            careerId={CareerId.Nanny}
            stepAction="Пройдите тест"
            stepNumber={meta.stepNumber}
            totalSteps={meta.totalSteps}
          />
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 5, mb: 2.5 }}>
            <Typography variant="button">{model.question}</Typography>
            <Typography color={theme.palette.text.secondary} variant="caption">
              {questionSuggest}
            </Typography>
          </Box>
          <FieldComponent name={String(model.id)} variants={model.variants} />
        </StepContainer>
        <StepFooter state={state}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
            {state && <StepResult state={state} />}
            <Button
              color={state ? 'gray' : 'pink'}
              disabled={!form.formState.isValid}
              sx={{ width: { xs: '100%', [MOBILE_BREAKPOINT_THRESHOLD]: 'auto' } }}
              type="submit"
            >
              Далее
            </Button>
          </Box>
        </StepFooter>
      </Box>
    </AppFormProvider>
  );
};
